import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { openDashboardDialog } from '@actions/dashboard-actions';
import { reorderWorkflowTaskType } from '@actions/workflow-actions';
import * as colors from '@constants/colors';
import * as dialog from '@constants/dialogs';
import { IconButton } from '@mui';
import AddIcon from '@material-ui/icons/Add';
import AddWorkflowTaskTypeDialog from '@shared/dialogs/add-workflow-task-type-dialog';
import DeleteWorkflowTaskTypeDialog from '@shared/dialogs/delete-workflow-task-type-dialog';
import { getWorkflowTaskTypes } from '@utils/workflow-utils';
import TaskTypeRow from './workflow-drawer-task-type-row';
import './details-layout-drawer.scss';

const WorkflowDrawer = ({ selectedTaskType, setSelectedTaskType }) => {
  const { id } = useParams();
  const { data, loading } = useSelector(state => state.dataDetail);
  const { task_type: task_types } = useSelector(state => state.dataTypes);
  const [droppedTaskType, setDroppedTaskType] = useState(null);  // Last moved (dropped) task template row.
  const [isDragging, setIsDragging] = useState(false);

  const dispatch = useDispatch();

  const taskTypes = useMemo(() => getWorkflowTaskTypes(data, task_types, droppedTaskType), [data, droppedTaskType, task_types]);

  useEffect(() => {
    // Set the first available task template as the selected one if none is selected.
    if (!selectedTaskType && taskTypes.length > 0) {
      setSelectedTaskType(taskTypes[0]);
    }
  }, [selectedTaskType, setSelectedTaskType, taskTypes]);

  // Drag-and-dropped trick:
  // (See the useEffect code in TaskTypeDrawerList for an explanation).
  useEffect(() => {
    if (loading === false) {
      setDroppedTaskType(null);
    }
  }, [loading, setDroppedTaskType]);

  // Monitor for all drag events to handle the drop
  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data.type === 'workflow-task-type',
      onDragStart: () => setIsDragging(true),
      onDrop: ({ source, location }) => {
        setIsDragging(false);

        const destination = location.current.dropTargets[0];
        if (!destination) {
          return;
        }

        const sourceIndex = source.data.index;
        const destinationIndex = destination.data.index;
        const edge = extractClosestEdge(destination.data);

        // Calculate the new order
        let newIndex = destinationIndex;
        if (edge === 'bottom') {
          newIndex = destinationIndex + 1;
        }

        // Adjust if moving down (source is above destination)
        if (sourceIndex < newIndex) {
          newIndex -= 1;
        }

        // Don't do anything if dropped in the same position
        if (sourceIndex === newIndex) {
          return;
        }

        const taskType = taskTypes[sourceIndex];  // Get dragged task template row.
        const new_order = newIndex + 1;  // Set new order value for this task template row (1-based).
        setDroppedTaskType({ ...taskType, order: new_order });  // Set it on the state to display the new order until data is reloaded.

        const payload = { new_order, order: taskType.order };

        // Call backend to save the new order:
        dispatch(reorderWorkflowTaskType(id, payload));
      }
    });
  }, [dispatch, id, taskTypes]);

  if (!data) {
    return null;
  }

  const onAdd = () => dispatch(openDashboardDialog(dialog.ADD_WORKFLOW_TASK_TYPE, { id, workflow: data }));

  const onDelete = taskType => {
    setSelectedTaskType(taskType);
    dispatch(openDashboardDialog(dialog.DELETE_WORKFLOW_TASK_TYPE, { id, taskType, workflowName: data.name }));
  };

  return (
    <Fragment>
      <div data-testid="workflow-drawer-header" styleName="header row">
        <div styleName="row-left">Task templates</div>
        <div styleName="row-right">
          <IconButton onClick={onAdd} style={{ padding: 0 }}>
            <AddIcon />
          </IconButton>
        </div>
      </div>
      {selectedTaskType && (
        <div
          data-testid="workflow-drawer-droppable"
          style={{
            border: isDragging ? `2px dashed ${colors.dotmapsBlue}` : '2px solid white',
            backgroundColor: isDragging ? '#E9F0FD' : colors.overlayTextColor,
            boxSizing: 'border-box',
            width: '100%'
          }}
        >
          {taskTypes.map((taskType, index) => (
            <TaskTypeRow
              index={index}
              key={taskType.id}
              onDelete={onDelete}
              selectedTaskType={selectedTaskType}
              setSelectedTaskType={setSelectedTaskType}
              taskType={taskType}
            />
          ))}
        </div>
      )}
      <AddWorkflowTaskTypeDialog />
      <DeleteWorkflowTaskTypeDialog />
    </Fragment>
  );
};

WorkflowDrawer.propTypes = {
  selectedTaskType: PropTypes.object,
  setSelectedTaskType: PropTypes.func
};

export default WorkflowDrawer;
